// Prompt card for /prompt-gallery. Rendered via `Grid.children` returning
// the PROMPTS array — each prompt object's properties (id, category,
// title, tagline, prompt) merge onto the card root, so children read
// them via `el.parent.X` (the standard MarketplaceCard pattern).
//
// "Copied!" feedback flashes through the page state key
// `_promptGalleryCopiedId` (set on click, cleared 1.6s later). Single
// shared key is fine because copying a second card before the first
// timer fires is exactly the case where the new flash should win.

const _flashCopied = (el, s, id) => {
  s.update({ _promptGalleryCopiedId: id })
  if (el.__copyTimer) clearTimeout(el.__copyTimer)
  el.__copyTimer = setTimeout(() => {
    s.update({ _promptGalleryCopiedId: null })
  }, 1600)
}

export const PromptGalleryCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  borderRadius: 'A',
  border: '1px solid line',
  background: 'dialog',
  transition: 'Z defaultBezier border-color',
  ':hover': {
    borderColor: 'blue'
  },

  CardCategory: {
    tag: 'span',
    text: (el) => {
      const id = el.parent.category
      // Capitalize for display — categories are stored as kebab-case slugs.
      return id.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    fontSize: 'X',
    fontWeight: '600',
    color: 'blue',
    textTransform: 'uppercase',
    letterSpacing: 'X'
  },

  CardTitle: {
    tag: 'h4',
    margin: '0',
    fontSize: 'A',
    fontWeight: '700',
    color: 'title',
    text: (el) => el.parent.title
  },

  CardTagline: {
    tag: 'p',
    margin: '0',
    fontSize: 'Y2',
    color: 'caption',
    lineHeight: '1.4',
    text: (el) => el.parent.tagline
  },

  // Pre block keeps newlines/code fences in the prompt readable.
  // `whiteSpace: pre-wrap` lets long lines wrap; `maxHeight` + `overflow:
  // auto` caps height so the grid stays balanced — clicking Copy still
  // grabs the full prompt.
  CardPromptBox: {
    tag: 'pre',
    margin: '0',
    padding: 'Z',
    borderRadius: 'Z',
    background: 'document',
    border: '1px solid line',
    fontSize: 'Y',
    color: 'title',
    fontFamily: 'monospace',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    overflow: 'auto',
    maxHeight: 'F',
    text: (el) => el.parent.prompt
  },

  CardFooter: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    marginTop: 'auto',

    CardCharCount: {
      tag: 'span',
      fontSize: 'X',
      color: 'caption',
      text: (el) => `${el.parent.parent.prompt.length} chars`
    },

    CopyBtn: {
      extends: 'Button',
      type: 'button',
      text: (el, s) => (s._promptGalleryCopiedId === el.parent.parent.id ? 'Copied!' : 'Copy prompt'),
      title: 'Copy prompt to clipboard',
      onClick: async (e, el, s) => {
        e.stopPropagation()
        const card = el.parent.parent
        const text = card.prompt
        if (!text || typeof navigator === 'undefined') return
        try {
          await navigator.clipboard.writeText(text)
          _flashCopied(el, s, card.id)
        } catch {
          // Clipboard permission may be denied — silently no-op so the
          // user notices the click did nothing rather than a false success.
        }
      }
    }
  }
}
